import { BRAND_NAME } from "@/utils/constants";
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import Image from "next/image";
import { useRouter } from "next/router";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import { Pagination } from "swiper";
import { Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import "swiper/css";
import "swiper/css/pagination";
import styles from "../../styles/shap_of_you.module.scss";
import { ProductViewScreenProps, DesktopStep } from "./types";
import ProductPageDefault from "../../assets/images/ProductPage_Default_Selection.png";
import nak from "../../assets/images/nekl_s.png";
import { AppState, useAppDispatch } from "@/store";
import { selectShapeOfYou, shapeOfYouActions } from "@/store/slices/shapeOfYou";
import {
  fetchSimilarProducts,
  fetchSimilarProductFilters,
} from "@/store/slices/category";
import { selectCart, fetchCartItems } from "@/store/slices/cart";
import { selectAuth, authActions } from "@/store/slices/auth";
import httpClient from "@/api/http-client";
import { ptrackEvent } from "@/lib/ppixel";
import { GA4_ITEM_BRAND } from "@/lib/ga4";
import endpoints from "@/api/endpoints";
import { unbxdCusEngTrackEvent } from "@/lib/unbxd";
import jel from "../../assets/images/jel.png";
import filterIcon from "../../assets/images/icons/filter-icon.svg";
import cartAddedIcon from "../../assets/svg/Icons.svg";
import ProductDetailsModal from "./ProductDetailsModal";
import FilterModal from "./FilterModal";
import CartModal from "./CartModal";
import LiveCameraPermissionModal from "./LiveCameraPermissionModal";

const ProductViewScreen: React.FC<ProductViewScreenProps> = ({
  navigateToStep,
  filter,
  setFilter,
  logStep,
}) => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { recommendations, capturedImage } = useSelector(selectShapeOfYou);
  const categoryState = useSelector((state: AppState) => state.category);
  const cartState = useSelector(selectCart);
  const authState = useSelector(selectAuth);

  const [activeCategory, setActiveCategory] = useState<string>("Necklace");
  const [loading, setLoading] = useState<boolean>(false);
  const [productIds, setProductIds] = useState<string[]>([]);
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  const [selected, setSelected] = useState<number | string>("");

  // Virtual try-on image state
  const [virtualTryOnImage, setVirtualTryOnImage] = useState<string | null>(
    null
  );
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);

  // Track selected products per category
  const [selectedEarringId, setSelectedEarringId] = useState<string | null>(
    null
  );
  const [selectedNecklaceId, setSelectedNecklaceId] = useState<string | null>(
    null
  );

  // Live mode state
  const [isLiveMode, setIsLiveMode] = useState(false);
  const liveModeIframeRef = useRef<HTMLIFrameElement>(null);
  // Bump this to force a re-sync of the iframe when selection changes
  const [liveModeSyncSeq, setLiveModeSyncSeq] = useState(0);
  const swiperRef = useRef<SwiperType | null>(null);

  // Cart action loading state
  const [cartActionLoading, setCartActionLoading] = useState(false);
  const [showAddedSuccess, setShowAddedSuccess] = useState(false);

  // Live mode camera permission state
  const [showCameraPermissionModal, setShowCameraPermissionModal] =
    useState(false);
  const [cameraPermissionChecked, setCameraPermissionChecked] = useState(false);

  // Call image augmentation API (following Product.tsx pattern)
  const callImageAPI = useCallback(
    async (earringId?: string | null, necklaceId?: string | null) => {
      if (!capturedImage) {
        return;
      }

      // Use provided IDs or fall back to state
      const earringProductId =
        earringId !== undefined ? earringId : selectedEarringId;
      const necklaceProductId =
        necklaceId !== undefined ? necklaceId : selectedNecklaceId;

      // Only call API if at least one product is selected
      if (!earringProductId && !necklaceProductId) {
        return;
      }

      setIsGeneratingImage(true);

      try {
        const requestBody: any = {
          image_data: capturedImage,
        };

        // Add earring product ID if available
        if (earringProductId) {
          requestBody.earring_product_id = earringProductId;
        }

        // Add necklace product ID if available
        if (necklaceProductId) {
          requestBody.necklace_product_id = necklaceProductId;
        }

        const response = await httpClient.post(
          endpoints.shape_of_you.image(),
          requestBody
        );

        // Set the virtual try-on image from the API response
        if (response.data.success && response.data.image) {
          setVirtualTryOnImage(response.data.image);
          dispatch(shapeOfYouActions.setVirtualTryOnImage(response.data.image));
        }
      } catch (error) {
        toast.error("Error updating virtual try-on image");
        setVirtualTryOnImage(null);
        dispatch(shapeOfYouActions.setVirtualTryOnImage(null));
      } finally {
        setIsGeneratingImage(false);
      }
    },
    [capturedImage, selectedEarringId, selectedNecklaceId, dispatch]
  );
  // Stable ref so the fetch effect can call callImageAPI without it being a dep
  const callImageAPIRef = useRef(callImageAPI);
  callImageAPIRef.current = callImageAPI;

  const products = categoryState.similarProducts?.data || [];

  // Get product SKUs for selected products (matching desktop Product.tsx pattern)
  const getSelectedProductSkusByCategory = () => {
    const earringItems: { productId: string }[] = [];
    const necklaceItems: { productId: string }[] = [];

    if (selectedEarringId) {
      const earringProduct = products.find(
        (p: any) =>
          String(p.id) === String(selectedEarringId) ||
          String(p.product_id) === String(selectedEarringId) ||
          String(p.sku) === String(selectedEarringId)
      );
      const sku = String(earringProduct?.sku || selectedEarringId).trim();
      if (sku && sku !== "undefined" && sku !== "null") {
        earringItems.push({ productId: sku });
      }
    }

    if (selectedNecklaceId) {
      const necklaceProduct = products.find(
        (p: any) =>
          String(p.id) === String(selectedNecklaceId) ||
          String(p.product_id) === String(selectedNecklaceId) ||
          String(p.sku) === String(selectedNecklaceId)
      );
      const sku = String(necklaceProduct?.sku || selectedNecklaceId).trim();
      if (sku && sku !== "undefined" && sku !== "null") {
        necklaceItems.push({ productId: sku });
      }
    }

    return { earringItems, necklaceItems };
  };

  const getLiveModeTargetOrigin = () => {
    const src = liveModeIframeRef.current?.src;
    if (!src) return "https://jewel-aug.adamastech.org";
    try {
      return new URL(src).origin;
    } catch {
      return "https://jewel-aug.adamastech.org";
    }
  };

  // Helper function to send product to iframe with retry (matching desktop Product.tsx)
  const sendProductToIframe = (
    sku: string,
    category: "EARRING" | "NECKLACE",
    clearOtherCategory: boolean = false,
    otherCategoryHadValue: boolean = false
  ) => {
    const iframeWin = liveModeIframeRef.current?.contentWindow;
    if (!iframeWin) {
      console.log("Iframe window not available");
      return;
    }

    const targetOrigin = getLiveModeTargetOrigin();

    const sendMessage = () => {
      iframeWin.postMessage(
        {
          type: "overlay-products",
          category: category,
          items: [{ productId: sku }],
        },
        targetOrigin
      );
      console.log(`Sent ${category} to iframe:`, sku);

      if (clearOtherCategory && otherCategoryHadValue) {
        const otherCategory = category === "EARRING" ? "NECKLACE" : "EARRING";
        iframeWin.postMessage(
          {
            type: "overlay-products",
            category: otherCategory,
            items: [],
          },
          targetOrigin
        );
        console.log(`Cleared ${otherCategory} in iframe`);
      }
    };

    // Send once — postMessage is synchronous & reliable when iframe is loaded
    sendMessage();
  };

  // Track if we've logged product_viewed
  const hasLoggedProductView = useRef(false);

  // Log product_viewed when products are first displayed
  useEffect(() => {
    if (recommendations && logStep && !hasLoggedProductView.current) {
      logStep("product_viewed");
      hasLoggedProductView.current = true;
    }
  }, [recommendations, logStep]);

  // Determine available categories from recommendations
  useEffect(() => {
    if (recommendations) {
      const categories = Object.keys(recommendations).filter(
        (key) => key === "Earring" || key === "Necklace"
      ) as ("Earring" | "Necklace")[];
      setAvailableCategories(categories);

      // Set initial active category if not already set
      if (
        categories.length > 0 &&
        !categories.includes(activeCategory as "Earring" | "Necklace")
      ) {
        setActiveCategory(categories[0]);
      }
    }
  }, [recommendations, activeCategory]);

  // Fetch product IDs when category changes (following Product.tsx pattern)
  useEffect(() => {
    let cancelled = false;

    const fetchProductIds = async () => {
      if (!recommendations || !activeCategory) return;

      const categoryData =
        recommendations[activeCategory as keyof typeof recommendations];
      if (!categoryData) return;

      // Show loading immediately when category changes (prevents "No products available" flash)
      setLoading(true);
      setProductIds([]);

      try {
        const requestBody = {
          mode: activeCategory,
        };

        const response = await httpClient.post(
          endpoints.shape_of_you.products(),
          requestBody
        );

        if (cancelled) return;

        if (response && response.data && response.data.product_ids) {
          const ids = response.data.product_ids;
          setProductIds(ids);

          if (ids.length > 0) {
            // Fetch product details - await so loading stays true until products load
            const result = await dispatch(
              fetchSimilarProducts({
                query: {
                  skus: ids,
                  size: ids.length,
                  ...filter, // Include filter parameters (priceMin, priceMax, metal, occasions)
                },
              })
            );

            if (cancelled) return;

            // Fetch filter options (non-blocking)
            dispatch(
              fetchSimilarProductFilters({
                query: {
                  skus: ids,
                  size: ids.length,
                  ...filter, // Include filter parameters
                },
              })
            );

            // Use the actual first product's SKU from the fetch result so the
            // swiper's isSelected check (which uses product.sku) always matches.
            const fetchedProducts: any[] = (result as any)?.payload?.data || [];
            const firstProduct = fetchedProducts[0];

            // Only proceed with selection if a real product was found
            if (firstProduct) {
              const firstId = String(
                firstProduct.sku || firstProduct.id || firstProduct.product_id
              );

              // Auto-select first product
              setSelected(firstId);

              // Update category-specific ID and call image API only when a
              // matching product exists (firstId === product.sku in the swiper)
              if (activeCategory === "Earring") {
                setSelectedEarringId(firstId);
                if (capturedImage) {
                  callImageAPIRef.current(firstId, null);
                }
              } else if (activeCategory === "Necklace") {
                setSelectedNecklaceId(firstId);
                if (capturedImage) {
                  callImageAPIRef.current(null, firstId);
                }
              }
            }

            // Center the first item in swiper after it's initialized
            setTimeout(() => {
              if (swiperRef.current) {
                try {
                  const swiper = swiperRef.current;
                  if (swiper.params?.loop) {
                    swiper.slideToLoop(0, 0);
                  } else {
                    swiper.slideTo(0, 0);
                  }
                } catch (error) {
                  // Swiper not ready yet, ignore
                }
              }
            }, 200);
          }
        }
      } catch (error) {
        if (!cancelled) {
          toast.error("Failed to load products");
          setProductIds([]);
        }
      } finally {
        if (!cancelled) {
          // Increase loader time slightly so product display gets time to render properly
          setTimeout(() => {
            if (!cancelled) setLoading(false);
          }, 1500);
        }
      }
    };

    fetchProductIds();
    return () => {
      cancelled = true;
    };
  }, [activeCategory, recommendations, dispatch, filter, capturedImage]);

  // Send product data to iframe when live mode is active (matching desktop Product.tsx)
  useEffect(() => {
    if (!isLiveMode || !liveModeIframeRef.current) return;

    const sendProductDataOnce = () => {
      const iframeWin = liveModeIframeRef.current?.contentWindow;
      if (!iframeWin) return false;

      const { earringItems, necklaceItems } =
        getSelectedProductSkusByCategory();
      const targetOrigin = getLiveModeTargetOrigin();

      if (earringItems.length > 0) {
        iframeWin.postMessage(
          {
            type: "overlay-products",
            category: "EARRING",
            items: earringItems,
          },
          targetOrigin
        );
        console.log("Sent EARRING to iframe:", earringItems);
      } else {
        iframeWin.postMessage(
          { type: "overlay-products", category: "EARRING", items: [] },
          targetOrigin
        );
      }

      if (necklaceItems.length > 0) {
        iframeWin.postMessage(
          {
            type: "overlay-products",
            category: "NECKLACE",
            items: necklaceItems,
          },
          targetOrigin
        );
        console.log("Sent NECKLACE to iframe:", necklaceItems);
      } else {
        iframeWin.postMessage(
          { type: "overlay-products", category: "NECKLACE", items: [] },
          targetOrigin
        );
      }

      return true;
    };

    let retryTimeout: NodeJS.Timeout | null = null;
    const sendWithRetry = () => {
      if (retryTimeout) {
        clearTimeout(retryTimeout);
        retryTimeout = null;
      }

      sendProductDataOnce();

      // Single safety retry after 1s in case the first message was missed
      retryTimeout = setTimeout(() => {
        sendProductDataOnce();
        retryTimeout = null;
      }, 1000);
    };

    const iframe = liveModeIframeRef.current;
    if (iframe.contentDocument?.readyState === "complete") {
      sendWithRetry();
    } else {
      const onLoad = () => {
        setTimeout(() => {
          sendWithRetry();
        }, 300);
      };
      iframe.addEventListener("load", onLoad, { once: true });
    }

    return () => {
      if (retryTimeout) {
        clearTimeout(retryTimeout);
        retryTimeout = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLiveMode,
    selectedEarringId,
    selectedNecklaceId,
    products,
    liveModeSyncSeq,
  ]);

  // Get selected product details
  const selectedProduct = products.find(
    (p: any) =>
      String(p.id) === String(selected) ||
      String(p.product_id) === String(selected) ||
      String(p.sku) === String(selected)
  );

  // Check if selected product is in cart
  const cartItem = useMemo(
    () =>
      selectedProduct
        ? cartState.items.find(
            (v: any) =>
              v.identifier == selectedProduct.priceId?.toString() || ""
          ) || null
        : null,
    [cartState.items, selectedProduct]
  );

  // Handle category tab click (matching desktop handleTabClick pattern)
  const handleCategoryClick = (category: string) => {
    setActiveCategory(category);
    setSelected(""); // Reset selection when switching categories
    setVirtualTryOnImage(null); // Clear virtual try-on image when switching tabs
    dispatch(shapeOfYouActions.setVirtualTryOnImage(null));
    // Clear product IDs so first product of new tab will be set by the products effect
    setSelectedEarringId(null);
    setSelectedNecklaceId(null);
  };

  // Handle product selection and call image API
  const handleProductSelect = (
    product: any,
    productId: string,
    index: number
  ) => {
    setSelected(productId);

    // Log product selection (non-blocking)
    if (logStep) {
      logStep("product_selected", {
        success_message: "Product selected successfully",
        success_code: "200",
        response_status: 200,
        request_payload: {
          action: "product_click",
          product_id: productId,
          category: activeCategory,
          index: index,
        },
        response_payload: {
          product_id: productId,
          product_sku: product?.sku || null,
          product_title: product?.title || product?.slug || null,
          product_price: product?.price || null,
          product_category: activeCategory,
          product_image: product?.image || product?.images?.[0] || null,
          has_fast_delivery: product?.fastDelivery || false,
          is_trending: product?.trending || false,
          is_new_arrival: product?.new_arrival || false,
          selection_index: index,
        },
      });
    }

    // Slide to the selected item to center it
    if (swiperRef.current) {
      try {
        const swiper = swiperRef.current;
        if (swiper.params?.loop) {
          swiper.slideToLoop(index, 300);
        } else {
          swiper.slideTo(index, 300);
        }
      } catch (error) {
        // Swiper not ready, ignore
      }
    }

    // Determine which ID to update and whether to clear the other category
    let clearOtherCategory = false;
    let otherCategoryHadValue = false;

    if (activeCategory === "Earring") {
      setSelectedEarringId(productId);
      if (selectedNecklaceId) {
        setSelectedNecklaceId(null);
        clearOtherCategory = true;
        otherCategoryHadValue = true;
      }
      // Call image API with ONLY earring ID
      if (capturedImage) {
        callImageAPI(productId, null);
      }
    } else if (activeCategory === "Necklace") {
      setSelectedNecklaceId(productId);
      if (selectedEarringId) {
        setSelectedEarringId(null);
        clearOtherCategory = true;
        otherCategoryHadValue = true;
      }
      // Call image API with ONLY necklace ID
      if (capturedImage) {
        callImageAPI(null, productId);
      }
    }

    // Get the product SKU
    const sku = String(product.sku || productId).trim();

    // Send to iframe FIRST if in live mode (matching desktop Product.tsx)
    if (isLiveMode && sku && sku !== "undefined" && sku !== "null") {
      const category = activeCategory === "Earring" ? "EARRING" : "NECKLACE";
      sendProductToIframe(
        sku,
        category as "EARRING" | "NECKLACE",
        clearOtherCategory,
        otherCategoryHadValue
      );

      // Also trigger useEffect sync as backup
      setLiveModeSyncSeq((s) => s + 1);
    }
  };

  // Check camera permission and toggle live mode
  const handleLiveModeToggle = async () => {
    // If already in live mode, just turn it off
    if (isLiveMode) {
      setIsLiveMode(false);
      return;
    }

    // Trying to enable live mode - check camera permission
    try {
      const permissionStatus = await navigator.permissions.query({
        name: "camera" as PermissionName,
      });

      if (permissionStatus.state === "granted") {
        // Permission granted at browser level - also require persisted app flag
        const persisted = localStorage.getItem("cameraPermissionGranted");
        if (persisted === "true") {
          setIsLiveMode(true);
        } else {
          // Browser granted but app flag not set - show modal to confirm
          setShowCameraPermissionModal(true);
        }
      } else if (permissionStatus.state === "prompt") {
        // User hasn't made a decision yet - show modal to request
        setShowCameraPermissionModal(true);
      } else if (permissionStatus.state === "denied") {
        // User has previously denied permission
        toast.error(
          "Camera permission denied. Please enable it in your browser settings."
        );
      }
    } catch (error) {
      // Permissions API not supported - show modal to request
      console.log("Permissions API not supported, showing request modal");
      setShowCameraPermissionModal(true);
    }
  };

  // Handle camera permission allowed
  const handleCameraPermissionAllowed = () => {
    setShowCameraPermissionModal(false);
    setIsLiveMode(true);
    toast.success("Camera access granted. Live View enabled.");
  };

  // Handle camera permission denied
  const handleCameraPermissionDenied = () => {
    setShowCameraPermissionModal(false);
    setIsLiveMode(false);
    toast.error("Camera permission denied. Switched back to Photo mode.");
  };

  // Add to cart function
  const addToCart = async (redirect = false) => {
    if (!selectedProduct) {
      toast.error("Please select a product first");
      return;
    }

    if (!authState.user) {
      dispatch(authActions.setIsModalOpen(true));
      return;
    }

    setCartActionLoading(true);

    try {
      await httpClient.post(endpoints.cart.add(), {
        is_solitaire: false,
        type: "product",
        identifier: selectedProduct.priceId,
      });

      await dispatch(fetchCartItems());

      // Show success state momentarily
      setCartActionLoading(false);
      setShowAddedSuccess(true);

      // Hide success state after 1.5 seconds
      setTimeout(() => {
        setShowAddedSuccess(false);
      }, 1500);

      toast.success("Item added to cart");

      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: "add_to_cart",
        ecommerce: {
          currency: "INR",
          value: selectedProduct.price,
          items: [
            {
              item_id:
                selectedProduct.id ||
                selectedProduct.product_id ||
                selectedProduct.sku,
              item_name: selectedProduct.title || selectedProduct.slug,
              item_brand: GA4_ITEM_BRAND,
              item_category: activeCategory,
              item_variant:
                selectedProduct.purity ||
                selectedProduct.purities?.[0] ||
                selectedProduct.priceId,
              price: selectedProduct.price,
              quantity: 1,
            },
          ],
        },
      });

      unbxdCusEngTrackEvent("Add to cart ", {
        source: "web",
        platform: "senco",
        prid: selectedProduct.id || selectedProduct.product_id,
        variant_id: selectedProduct.priceId,
        image_url:
          selectedProduct.image ||
          (selectedProduct.images && selectedProduct.images[0]),
        prqt: 1,
        product_name: selectedProduct.title || selectedProduct.slug,
        category: activeCategory || "",
        brand: BRAND_NAME,
        amount: selectedProduct.price,
        product_url: `${window.location.origin}/jewellery/${selectedProduct.slug}`,
        discounted_price: selectedProduct.discounted_price || 0,
        stock_availability: 1,
      });

      // Log product added to cart (non-blocking)
      if (logStep) {
        logStep("product_added_to_cart", {
          api_endpoint: endpoints.cart.add(),
          request_payload: {
            is_solitaire: false,
            type: "product",
            identifier: selectedProduct.priceId,
          },
          response_status: 200,
          success_message: "Item added to cart successfully",
          response_payload: {
            product_id: selectedProduct.id || selectedProduct.product_id,
            product_sku: selectedProduct.sku,
            product_title: selectedProduct.title || selectedProduct.slug,
            product_price: selectedProduct.price,
            category: activeCategory,
          },
        });

        // Also log session completed (Shape of You flow completed successfully)
        logStep("session_completed", {
          success_message:
            "Shape of You session completed - Product added to cart",
          success_code: "SESSION_COMPLETED",
          response_status: 200,
          request_payload: {
            completion_action: "add_to_cart",
            session_stage: "product_view",
          },
          response_payload: {
            completion_type: "product_added_to_cart",
            product_id: selectedProduct.id || selectedProduct.product_id,
            product_sku: selectedProduct.sku,
            product_title: selectedProduct.title || selectedProduct.slug,
            product_price: selectedProduct.price,
            product_category: activeCategory,
            cart_item_identifier: selectedProduct.priceId,
            completed_at: new Date().toISOString(),
          },
        });
      }

      if (redirect) {
        router.push("/cart");
      }
    } catch (error: any) {
      if (Number(error?.response?.status) === 400) {
        toast.error("Something went wrong! Refreshing the page!");
        window.location.reload();
      } else {
        toast.error("Failed to add item to cart");
      }
      setCartActionLoading(false);
    }
  };

  // Remove from cart function
  const removeFromCart = async () => {
    if (!confirm("Are you sure you want to remove this item from cart?"))
      return;

    setCartActionLoading(true);

    try {
      await httpClient.delete(endpoints.cart.remove(cartItem?.id));
      await dispatch(fetchCartItems());
      toast.info("Item removed from cart");
    } catch (error) {
      toast.error("Failed to remove item from cart");
    } finally {
      setCartActionLoading(false);
    }
  };

  // Check if any filters are active
  const hasActiveFilters = () => {
    return (
      (filter.metal && filter.metal.length > 0) ||
      (filter.occasions && filter.occasions.length > 0) ||
      filter.priceMin !== null ||
      filter.priceMax !== null ||
      (filter.priceRange !== null && filter.priceRange !== "")
    );
  };

  // Get image source with fallback
  const getImageSrc = (product: any) => {
    if (!product) return nak.src;

    if (product.image && typeof product.image === "string") {
      return product.image;
    }

    if (
      product.images &&
      Array.isArray(product.images) &&
      product.images.length > 0
    ) {
      if (typeof product.images[0] === "string") {
        return product.images[0];
      } else if (product.images[0] && product.images[0].url) {
        return product.images[0].url;
      }
    }

    if (product.image_url) return product.image_url;
    if (product.primary_image) return product.primary_image;
    if (product.thumbnail) return product.thumbnail;

    return nak.src;
  };
  const [isOpen, setIsOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isPanelExpanded, setIsPanelExpanded] = useState(true);

  useEffect(() => {
    document.body.style.overflow =
      isOpen || isFilterOpen || isCartOpen ? "hidden" : "auto";

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen, isFilterOpen, isCartOpen]);

  return (
    <>
      <div className="d-none d-md-block w-100">
        <div className={styles.container_desktop}>
          <div className={styles.image_main}>
            {isLiveMode ? (
              // Show Live Mode iframe
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  height: "100%",
                }}
              >
                <iframe
                  ref={liveModeIframeRef}
                  id="live-mode-frame"
                  src="https://jewel-aug.adamastech.org/shape-of-you/overlay-preview-alt"
                  title="Live Mode Overlay"
                  allow="camera; microphone; autoplay"
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "none",
                    background: "transparent",
                    display: "block",
                    pointerEvents: "none",
                  }}
                />
              </div>
            ) : virtualTryOnImage ? (
              // Show virtual try-on image if available
              <Image
                src={virtualTryOnImage}
                alt="Virtual Try-On"
                className={styles.bgImage}
                layout="fill"
                objectFit="cover"
                unoptimized
              />
            ) : capturedImage ? (
              // Show captured image if no virtual try-on yet
              <Image
                src={capturedImage}
                alt="Your Photo"
                className={styles.bgImage}
                layout="fill"
                objectFit="cover"
                unoptimized
              />
            ) : (
              // Fallback to default image
              <Image
                src={ProductPageDefault}
                alt="Background"
                className={styles.bgImage}
              />
            )}

            {/* Loading overlay on top of image */}
            {isGeneratingImage && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(0, 0, 0, 0.4)",
                  zIndex: 5,
                }}
              >
                <div style={{ textAlign: "center", color: "#fff" }}>
                  <Spinner animation="border" role="status" />
                  <p style={{ marginTop: "1rem", fontSize: "14px" }}>
                    Generating virtual try-on...
                  </p>
                </div>
              </div>
            )}
          </div>
          {/* Top Switch */}
          {!isOpen && (
            <div className={styles.topSwitch}>
              <button
                className={styles.back_arrow}
                onClick={() => {
                  if (logStep) {
                    logStep("product_view_back_clicked");
                  }
                  navigateToStep("uploadOrScan");
                }}
              >
                <i className="bi bi-arrow-left"></i>
              </button>
              <div className={styles.switch}>
                <span
                  className={isLiveMode ? styles.active : styles.live}
                  onClick={handleLiveModeToggle}
                  style={{ cursor: "pointer" }}
                >
                  Live View
                </span>
                <span
                  className={!isLiveMode ? styles.active : styles.live}
                  onClick={handleLiveModeToggle}
                  style={{ cursor: "pointer" }}
                >
                  Photo
                </span>
              </div>
              <div className={styles.filter_top_cta}>
                <div>
                  <span
                    className={styles.filter}
                    onClick={() => setIsFilterOpen(true)}
                    style={{ position: "relative", cursor: "pointer" }}
                  >
                    {hasActiveFilters() ? (
                      <Image
                        src={filterIcon}
                        alt="Filter Icon"
                        width={24}
                        height={24}
                      />
                    ) : (
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M4 10C4 10.5304 4.21071 11.0391 4.58579 11.4142C4.96086 11.7893 5.46957 12 6 12C6.53043 12 7.03914 11.7893 7.41421 11.4142C7.78929 11.0391 8 10.5304 8 10C8 9.46957 7.78929 8.96086 7.41421 8.58579C7.03914 8.21071 6.53043 8 6 8C5.46957 8 4.96086 8.21071 4.58579 8.58579C4.21071 8.96086 4 9.46957 4 10Z"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M6 4V8"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M6 12V20"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M10 16C10 16.5304 10.2107 17.0391 10.5858 17.4142C10.9609 17.7893 11.4696 18 12 18C12.5304 18 13.0391 17.7893 13.4142 17.4142C13.7893 17.0391 14 16.5304 14 16C14 15.4696 13.7893 14.9609 13.4142 14.5858C13.0391 14.2107 12.5304 14 12 14C11.4696 14 10.9609 14.2107 10.5858 14.5858C10.2107 14.9609 10 15.4696 10 16Z"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M12 4V14"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M12 18V20"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M16 7C16 7.53043 16.2107 8.03914 16.5858 8.41421C16.9609 8.78929 17.4696 9 18 9C18.5304 9 19.0391 8.78929 19.4142 8.41421C19.7893 8.03914 20 7.53043 20 7C20 6.46957 19.7893 5.96086 19.4142 5.58579C19.0391 5.21071 18.5304 5 18 5C17.4696 5 16.9609 5.21071 16.5858 5.58579C16.2107 5.96086 16 6.46957 16 7Z"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M18 4V5"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M18 9V20"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                    Filter
                  </span>
                </div>
                <div
                  className={styles.cartIcon}
                  style={{ position: "relative", cursor: "pointer" }}
                  onClick={() => setIsCartOpen(true)}
                >
                  Cart
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4 19C4 19.5304 4.21071 20.0391 4.58579 20.4142C4.96086 20.7893 5.46957 21 6 21C6.53043 21 7.03914 20.7893 7.41421 20.4142C7.78929 20.0391 8 19.5304 8 19C8 18.4696 7.78929 17.9609 7.41421 17.5858C7.03914 17.2107 6.53043 17 6 17C5.46957 17 4.96086 17.2107 4.58579 17.5858C4.21071 17.9609 4 18.4696 4 19Z"
                      stroke="#262121"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M15 19C15 19.5304 15.2107 20.0391 15.5858 20.4142C15.9609 20.7893 16.4696 21 17 21C17.5304 21 18.0391 20.7893 18.4142 20.4142C18.7893 20.0391 19 19.5304 19 19C19 18.4696 18.7893 17.9609 18.4142 17.5858C18.0391 17.2107 17.5304 17 17 17C16.4696 17 15.9609 17.2107 15.5858 17.5858C15.2107 17.9609 15 18.4696 15 19Z"
                      stroke="#262121"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M17 17H6V3H4"
                      stroke="#262121"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M6 5L20 6L19 13H6"
                      stroke="#262121"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {!!cartState.items.length && (
                    <div
                      style={{
                        position: "absolute",
                        top: "7px",
                        right: "29px",
                        background: "#c91723",
                        color: "white",
                        borderRadius: "50%",
                        width: "20px",
                        height: "20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "12px",
                        fontWeight: "600",
                      }}
                    >
                      {cartState.items.length}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Main Image */}
          {/* {loading && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(0, 0, 0, 0.7)",
                zIndex: 10,
              }}
            >
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  border: "3px solid rgba(255, 255, 255, 0.3)",
                  borderRadius: "50%",
                  borderTopColor: "#fff",
                  animation: "spin 1s linear infinite",
                }}
              >
                <style>
                  {\`
                    @keyframes spin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                  \`}
                </style>
              </div>
              <p style={{ marginTop: "1rem", fontSize: "14px", color: "#e2e2e2" }}>
                Loading {activeCategory.toLowerCase()}...
              </p>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ opacity: 0.7 }}
              >
                <path d="M6 9L12 15L18 9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          )} */}

          {/* Panel Toggle Button */}
          {!isOpen && (
            <div
              onClick={() => setIsPanelExpanded(!isPanelExpanded)}
              style={{
                position: "absolute",
                bottom: isPanelExpanded ? "230px" : "20px",
                left: "50%",
                transform: "translateX(-50%)",
                cursor: "pointer",
                zIndex: 10,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                transition: "bottom 0.4s ease",
              }}
            >
              {isPanelExpanded ? (
                // Down arrow (expanded → click to collapse)
                <svg
                  width="173"
                  height="36"
                  viewBox="0 0 173 36"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <foreignObject x="-6" y="-5.99998" width="185" height="48">
                    <div
                      style={{
                        backdropFilter: "blur(3px)",
                        clipPath: "url(#bgblur_pv_expand_clip)",
                        height: "100%",
                        width: "100%",
                      }}
                    ></div>
                  </foreignObject>
                  <g data-figma-bg-blur-radius="6">
                    <path
                      d="M92.4653 20L86.4998 24L80.5343 20"
                      stroke="white"
                      strokeOpacity="0.31"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M93.957 13.25L86.5001 18.25L79.0432 13.25"
                      stroke="white"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                  <defs>
                    <clipPath
                      id="bgblur_pv_expand_clip"
                      transform="translate(6 5.99998)"
                    >
                      <path d="M173 24C173 30.6274 167.627 36 161 36L12 36C5.37259 36 4.69686e-07 30.6274 1.04907e-06 24L2.09815e-06 12C2.67753e-06 5.37258 5.37259 4.69686e-07 12 1.04907e-06L161 1.40751e-05C167.627 1.46545e-05 173 5.3726 173 12L173 24Z" />
                    </clipPath>
                  </defs>
                </svg>
              ) : (
                // Up arrow (collapsed → click to expand)
                <svg
                  width="24"
                  height="20"
                  viewBox="0 0 18 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M2.96094 5.5L8.80471 1.5L14.6485 5.5"
                    stroke="white"
                    strokeOpacity="0.31"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M1.5 12.25L8.80471 7.25L16.1094 12.25"
                    stroke="white"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
          )}

          {/* Price & Filter */}
          {!isOpen && (
            <div
              className={styles.priceRow}
              style={{
                transition: "opacity 0.4s ease, transform 0.4s ease",
                opacity: isPanelExpanded ? 1 : 0,
                transform: isPanelExpanded
                  ? "translateY(0)"
                  : "translateY(20px)",
                pointerEvents: isPanelExpanded ? "auto" : "none",
              }}
            >
              <div className={styles.price}>
                ₹
                {selectedProduct
                  ? (
                      selectedProduct?.discount?.total ||
                      selectedProduct.price ||
                      0
                    ).toLocaleString("en-IN")
                  : "0"}
              </div>
              {/* <div
                onClick={() => selectedProduct && setIsOpen(true)}
                style={{ cursor: "pointer" }}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M17 7L7 17"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M8 7H17V16"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div> */}
              <div
                className={styles.details_open}
                onClick={() => selectedProduct && setIsOpen(true)}
                style={{
                  cursor: "pointer",
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 9H12.01"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M11 12H12V16H13"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 3C19.2 3 21 4.8 21 12C21 19.2 19.2 21 12 21C4.8 21 3 19.2 3 12C3 4.8 4.8 3 12 3Z"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Details
              </div>
            </div>
          )}

          {/* Thumbnails */}
          <div
            className={styles.thumbnailRow}
            style={{
              transition: "opacity 0.4s ease, transform 0.4s ease",
              opacity: isPanelExpanded ? 1 : 0,
              transform: isPanelExpanded ? "translateY(0)" : "translateY(20px)",
              pointerEvents: isPanelExpanded ? "auto" : "none",
            }}
          >
            <div className={styles.thumbnailRow_container}>
              {loading ? (
                <div style={{ textAlign: "center", padding: "20px" }}>
                  {/* <Spinner animation="border" size="sm" /> */}
                </div>
              ) : products.length > 0 ? (
                <Swiper
                  onSwiper={(swiper) => {
                    swiperRef.current = swiper;
                  }}
                  key={activeCategory}
                  slidesPerView={5}
                  centeredSlides={true}
                  loop={products.length > 5}
                  spaceBetween={10}
                  pagination={{ clickable: true }}
                  modules={[Pagination]}
                  className={styles.mySwiper}
                >
                  {products.map((product: any, index: number) => {
                    const productId = String(product.sku);
                    const isSelected =
                      String(selected) === productId ||
                      String(selected) === String(product.id) ||
                      String(selected) === String(product.product_id);
                    return (
                      <SwiperSlide key={productId}>
                        <div
                          className={`${styles.slide} ${
                            isSelected ? styles.selected : ""
                          }`}
                          onClick={() =>
                            handleProductSelect(product, productId, index)
                          }
                          style={{ cursor: "pointer" }}
                        >
                          <Image
                            src={getImageSrc(product)}
                            alt={product.name || "Product"}
                            width={80}
                            height={80}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = nak.src;
                            }}
                          />
                        </div>
                      </SwiperSlide>
                    );
                  })}
                </Swiper>
              ) : (
                <div
                  style={{
                    textAlign: "center",
                    padding: "20px",
                    color: "#fff",
                  }}
                >
                  No products available
                </div>
              )}
            </div>
          </div>

          {/* Category Tabs */}
          <div
            className={styles.categoryTabs}
            style={{
              transition: "opacity 0.4s ease, transform 0.4s ease",
              opacity: isPanelExpanded ? 1 : 0,
              transform: isPanelExpanded ? "translateY(0)" : "translateY(20px)",
              pointerEvents: isPanelExpanded ? "auto" : "none",
            }}
          >
            <div className={styles.tabs}>
              {availableCategories.includes("Earring") && (
                <button
                  className={`${styles.tab} ${
                    activeCategory === "Earring" ? styles.active : ""
                  }`}
                  onClick={() => handleCategoryClick("Earring")}
                >
                  Earrings
                </button>
              )}

              {availableCategories.includes("Necklace") && (
                <button
                  className={`${styles.tab} ${
                    activeCategory === "Necklace" ? styles.active : ""
                  }`}
                  onClick={() => handleCategoryClick("Necklace")}
                >
                  Necklace
                </button>
              )}
            </div>
          </div>

          {/* Bottom Button */}
          <div
            className={styles.bottomBar}
            style={{
              transition: "opacity 0.4s ease, transform 0.4s ease",
              opacity: isPanelExpanded ? 1 : 0,
              transform: isPanelExpanded ? "translateY(0)" : "translateY(20px)",
              pointerEvents: isPanelExpanded ? "auto" : "none",
            }}
          >
            <div
              className={styles.share}
              onClick={() => navigateToStep("shareView")}
              style={{ cursor: "pointer" }}
            >
              <svg
                width="18"
                height="16"
                viewBox="0 0 18 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10.0002 0V4C3.42519 5.028 0.980188 10.788 0.000188363 16C-0.0368116 16.206 5.38419 10.038 10.0002 10V14L18.0002 7L10.0002 0Z"
                  fill="white"
                />
              </svg>
            </div>
            {showAddedSuccess ? (
              <button
                className={styles.cartBtn}
                disabled={true}
                style={{
                  backgroundColor: "#28a745",
                  cursor: "not-allowed",
                  whiteSpace: "nowrap",
                }}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M20 6L9 17L4 12"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>{" "}
                Added Successfully
              </button>
            ) : cartItem ? (
              <button
                className={styles.cartBtn}
                disabled
                style={{
                  backgroundColor: "#22c55e",
                  cursor: "default",
                }}
              >
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    whiteSpace: "nowrap",
                  }}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M20 6L9 17L4 12"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Added To Cart
                </span>
              </button>
            ) : (
              <button
                className={styles.cartBtn}
                onClick={() => addToCart(false)}
                disabled={cartActionLoading || !selectedProduct}
                style={{
                  backgroundColor: cartActionLoading ? "#999" : undefined,
                  cursor:
                    cartActionLoading || !selectedProduct
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                {cartActionLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Adding...
                  </>
                ) : (
                  <>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M4 19C4 19.5304 4.21071 20.0391 4.58579 20.4142C4.96086 20.7893 5.46957 21 6 21C6.53043 21 7.03914 20.7893 7.41421 20.4142C7.78929 20.0391 8 19.5304 8 19C8 18.4696 7.78929 17.9609 7.41421 17.5858C7.03914 17.2107 6.53043 17 6 17C5.46957 17 4.96086 17.2107 4.58579 17.5858C4.21071 17.9609 4 18.4696 4 19Z"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M12.5 17H6V3H4"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M6 5L20 6L19.14 12.017M16.5 13H6"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M16 19H22"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M19 16V22"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>{" "}
                    Add to Cart
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Product Details Modal */}
      <ProductDetailsModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        selectedProduct={selectedProduct}
        getImageSrc={getImageSrc}
        cartItem={cartItem}
        showAddedSuccess={showAddedSuccess}
        cartActionLoading={cartActionLoading}
        addToCart={addToCart}
        removeFromCart={removeFromCart}
      />

      {/* Filter Modal */}
      <FilterModal
        isOpen={isFilterOpen}
        setIsOpen={setIsFilterOpen}
        filter={filter}
        setFilter={setFilter}
        activeCategory={activeCategory}
      />

      {/* Cart Modal */}
      <CartModal isOpen={isCartOpen} setIsOpen={setIsCartOpen} />

      {/* Live Mode Camera Permission Modal */}
      <LiveCameraPermissionModal
        show={showCameraPermissionModal}
        onHide={() => setShowCameraPermissionModal(false)}
        onPermissionGranted={handleCameraPermissionAllowed}
        onPermissionDenied={handleCameraPermissionDenied}
        logStep={logStep}
      />
    </>
  );
};

export default ProductViewScreen;
